import { DEFAULT_GTM_HOST, type GtmConfig } from './config';
import { ensureDataLayer } from './pushEvent';

/**
 * Injects the GTM container script (and noscript fallback) for the given container ID.
 *
 * When `config.serverContainerUrl` is set, the script and noscript load from that
 * first-party server-side GTM container; otherwise they load from the default Google
 * host ({@link DEFAULT_GTM_HOST}) with no behavior change.
 *
 * The dataLayer is initialized once via {@link ensureDataLayer} before the snippet
 * loads and is never re-assigned.
 *
 * @see https://developers.google.com/tag-platform/tag-manager/server-side/send-data
 */
export function injectGtm(containerId: string, config: GtmConfig = {}): void {
    if (typeof window === 'undefined' || !containerId) return;

    if (document.querySelector(`script[data-gtm-id="${containerId}"]`)) return;

    ensureDataLayer();
    window.dataLayer?.push({ 'gtm.start': Date.now(), event: 'gtm.js' });

    const base = normalizeBase(config.serverContainerUrl) ?? `https://${DEFAULT_GTM_HOST}`;

    const script = document.createElement('script');
    script.async = true;
    script.dataset.gtmId = containerId;
    script.src = `${base}/gtm.js?id=${containerId}`;
    document.head.appendChild(script);

    const noscript = document.createElement('noscript');
    const iframe = document.createElement('iframe');
    iframe.src = `${base}/ns.html?id=${containerId}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    noscript.appendChild(iframe);
    document.body.appendChild(noscript);
}

/**
 * Returns the base URL (origin + path) of a server container URL, adding a default
 * `https://` scheme when absent and stripping any trailing slash and query/fragment.
 * The path is preserved (not dropped) because a same-origin sGTM container is commonly
 * hosted at a path, e.g. `https://one.shi.com/analytics` (AEGIS-599), not a subdomain.
 * Returns undefined for empty or invalid input.
 */
function normalizeBase(url?: string): string | undefined {
    if (!url) return undefined;
    const withScheme = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    try {
        const { origin, pathname } = new URL(withScheme);
        const path = pathname.replace(/\/+$/, '');
        return path && path !== '/' ? `${origin}${path}` : origin;
    } catch {
        return undefined;
    }
}

export function getDataLayer(): unknown[] {
    if (typeof window === 'undefined') return [];
    return window.dataLayer ?? [];
}
